import { NodeTypes, type Input } from "@/workflow_api/types/workflow";
import type {
  SerializeValueType,
  SubConditionData,
  Variable,
} from "../../if/types";

const cleanUUID = (variable: Variable): string => {
  const parts = variable.id.split("-");
  if (parts.length > 5) {
    return parts.slice(0, 5).join("-");
  }
  return variable.id;
};

export const getValueType = (type: SerializeValueType) => {
  if (!type) return "NONE";
  switch (type) {
    case "string":
    case "regex":
      return "STRING";
    case "positive-integer":
    case "number":
      return "NUMBER";
    case "variable":
      return "MODEL";
    case "boolean":
      return "BOOLEAN";
    case "date":
      return "DATE";
    case "array":
      return "ARRAY";
    default:
      return "NONE";
  }
};

const getSerializedVariable = (
  value: string | Variable | string[] | null,
  fallbackType: SerializeValueType,
  constantNum: number,
) => {
  if (value === null) {
    return {
      name: "var_switch_" + constantNum,
      type: getValueType(fallbackType),
      value: null,
    };
  }

  if (typeof value === "string") {
    return {
      name: "var_switch_" + constantNum,
      type: getValueType(fallbackType),
      value,
    };
  }

  const variable = value as Variable;

  if (variable.global_type) {
    if (variable.node_id === "global-variables") {
      return {
        name: "var_switch_" + constantNum,
        type: getValueType(variable.type ?? fallbackType),
        value: variable.id || null,
        global_type: variable.global_type,
      };
    }
    return {
      name: "var_switch_" + constantNum,
      type: getValueType(variable.type ?? fallbackType),
      value: variable.node_id || null,
      global_type: variable.global_type,
      connection: variable.node_id,
    };
  }

  if (
    variable.ref_name === NodeTypes.N8N ||
    variable.ref_name === NodeTypes.INTEROPERABILITY
  ) {
    return {
      name: "var_switch_" + constantNum,
      type: getValueType((value as Variable).type ?? fallbackType),
      value: null,
      connection: (value as Variable).node_id,
      output_name: (value as Variable).output_name,
    };
  }

  if (variable.id === variable.node_id) {
    return {
      name: "var_switch_" + constantNum,
      type: getValueType((value as Variable).type ?? fallbackType),
      value: null,
      connection: (value as Variable).node_id,
    };
  }

  if (variable.field_id && variable.output_name) {
    return {
      name: "var_switch_" + constantNum,
      type: getValueType((value as Variable).type ?? fallbackType),
      value: null,
      field: cleanUUID(value as Variable),
      connection: (value as Variable).node_id,
      output_name: (value as Variable).output_name,
    };
  }

  return {
    name: (value as Variable).name,
    type: getValueType((value as Variable).type ?? fallbackType),
    value: null,
    field: cleanUUID(value as Variable),
    connection: (value as Variable).node_id,
  };
};

/**
 * Parse the dynamic Condition information to a formated object to be saved
 * Notes:
 * 1. The first input of a condition is always a variable
 * 2. The second input can ve a variable or a const
 *    2.1 If it is a const, always will be the first const
 * TODO: add connection nodes
 * @param param0
 * @param refName
 * @returns
 */
export const serializeSwitchNode = (
  conditionsData: SubConditionData[],
): Input => {
  const conditions = conditionsData.map((condition) => {
    const inputs: Input = [];
    inputs.push(
      getSerializedVariable(
        condition.variable,
        condition.operation?.comparativeValueType,
        inputs.length + 1,
      ),
    );
    if (condition.operation?.comparativeValueType) {
      inputs.push(
        getSerializedVariable(
          condition.comparativeValue,
          condition.operation?.comparativeValueType,
          inputs.length + 1,
        ),
      );
    }
    return {
      name: condition.id || "",
      type: condition.operation?.name ?? "",
      inputs,
      operator: "AND",
    };
  });
  return conditions.map((c, index) => {
    return {
      name: `condition_${index + 1}`,
      value: null,
      type: "CONDITION",
      conditions: [c],
    };
  });
};
